import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SortingState } from "@tanstack/react-table";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import usePagination from "@/hooks/useSyncedPagination";
import { ActionDataBo } from "@/types/tableAction";
import { useGetAllExpeditionBo } from "@/api/expedition.api";
import { Button } from "@/components/ui/button";
import { DeletedExpeditionsTable } from "@/components/backOffice/validatedExpeditions/deletedExpeditionsTable";
import { DeletedExpeditionsFilters } from "@/components/backOffice/validatedExpeditions/deletedExpeditionsFilters";
import { useValidatedExpeditionsFilters } from "@/components/backOffice/validatedExpeditions/useValidatedExpeditionsFilters.reducer";
import { COUNTRY_REGIONS } from "@/constants/countries";
import { ValidatedExpeditionsModals } from "@/components/backOffice/validatedExpeditions/validatedExpeditionsModals";
import Paths from "@/constants/paths";

export const DeletedExpeditionsV2Page = () => {
  const { t } = useTranslation("boExpeditions");
  const navigate = useNavigate();
  const [actionData, setActionData] = useState<ActionDataBo | null>(null);
  const [sorting, setSorting] = useState<SortingState>([{ id: "deletedAt", desc: true }]);
  const [pagination, setPagination] = usePagination("deletedExpeditionsV2");
  const { filters, debouncedSearch, actions } = useValidatedExpeditionsFilters();

  const deletedAtFilter = useMemo(() => {
    if (!filters.deletedAt) return {};
    return {
      deletedAfter: filters.deletedAt[0].toISOString(),
      deletedBefore: filters.deletedAt[1].toISOString(),
    };
  }, [filters.deletedAt]);

  const expeditionQueryFilter = useMemo(
    () => ({
      isDeleted: true,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(filters.searchExact ? { searchExact: true } : {}),
      ...(filters.country
        ? { country: filters.country }
        : filters.region
          ? { countries: COUNTRY_REGIONS[filters.region] }
          : {}),
      ...(filters.customId ? { customId: filters.customId } : {}),
      ...(filters.packagingType ? { packagingType: filters.packagingType } : {}),
      ...(filters.finalizedById ? { finalizedById: filters.finalizedById } : {}),
      ...deletedAtFilter,
    }),
    [
      debouncedSearch,
      filters.searchExact,
      filters.country,
      filters.region,
      filters.customId,
      filters.packagingType,
      filters.finalizedById,
      deletedAtFilter,
    ],
  );

  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [expeditionQueryFilter, setPagination]);

  const { data: expeditions, isLoading: isExpeditionsLoading } = useGetAllExpeditionBo({
    ...expeditionQueryFilter,
    take: pagination.pageSize,
    skip: pagination.pageIndex * pagination.pageSize,
    sortBy: sorting[0]?.id,
    sortOrder: sorting[0]?.desc ? "DESC" : "ASC",
  });

  const expeditionsData = useMemo(() => expeditions?.data ?? [], [expeditions]);

  return (
    <div className="bg-border/20 flex h-fit min-h-dvh w-full flex-1 flex-col gap-4 p-8">
      <div className="flex w-full flex-col justify-between gap-2 md:flex-row">
        <div className="flex w-fit flex-col gap-2">
          <h1 className="font-jakarta-sans text-3xl font-semibold">{t("deletedPage.title")}</h1>
          <p>{t("deletedPage.subtitle")}</p>
        </div>
        <div className="flex flex-col items-end gap-4 py-2">
          <Button variant="outline" onClick={() => navigate(Paths.bo.validatedExpeditionsV2)}>
            <ArrowLeft className="h-4 w-4" />
            {t("deletedPage.backToExpeditions")}
          </Button>
        </div>
      </div>
      <DeletedExpeditionsFilters filters={filters} actions={actions} />
      <DeletedExpeditionsTable
        data={expeditionsData}
        rowCount={expeditions?.count || 0}
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        setActionData={setActionData}
        isLoading={isExpeditionsLoading}
      />
      <ValidatedExpeditionsModals
        actionData={actionData}
        onClose={() => setActionData(null)}
        setActionData={setActionData}
        isDeleted={true}
      />
    </div>
  );
};
